import {
  getSelectionDOMId,
  type Selection,
  type SelectionArea,
  SelectionContext
} from "@prismicio/editor-fields"
import {
  getOffsetTopRelativeTo,
  getScrollableAncestor
} from "@prismicio/editor-support/DOM"
import { useStableCallback } from "@prismicio/editor-support/React"
import { type ReactNode, useLayoutEffect, useMemo, useState } from "react"

import { headerBottomMargin, headerHeight } from "./documentLayout"
import { useDocumentParams, useGoToDocumentSelection } from "./documentRouting"

/**
 * Manages the SliceTable and DocumentEditor bi-directional selection.
 */

interface Props {
  children: ReactNode
}

export function SelectionProvider(props: Props) {
  const { children } = props

  const [selection, setSelection] = useState<SelectionId>({
    id: useDocumentParams().selectionId
  })

  const goToDocumentSelection = useGoToDocumentSelection()

  const update = useStableCallback(
    (requestedFrom: SelectionArea, id: string) => {
      const previousId = selection.id

      // The source of truth is the Context's id value.
      setSelection({ id, requestedFrom })

      // React-router is dumb and will still call history.replaceState even if nothing changed.
      if (previousId === id) return

      // But we also update the URL, so refreshing/bookmarking works.
      goToDocumentSelection(id)
    }
  )

  const selectionValue: Selection = useMemo(
    () => ({ id: selection.id, update }),
    [selection.id, update]
  )

  useScrollToSelection("sliceTable", selection)
  useScrollToSelection("sliceFieldSet", selection)

  return (
    <SelectionContext.Provider value={selectionValue}>
      {children}
    </SelectionContext.Provider>
  )
}

// As an object because we do want to force the scroll effect even if the id did not change.
interface SelectionId {
  id?: string
  requestedFrom?: SelectionArea
}

function useScrollToSelection(area: SelectionArea, selection: SelectionId) {
  useLayoutEffect(() => {
    const { id, requestedFrom } = selection
    if (!id) return

    // If there's no requestedFrom (mount time); both scrolls should activate.
    // If the selection request comes from one side, the intent is to highlight the other side.
    if (requestedFrom && requestedFrom === area) return

    const elementId = getSelectionDOMId(area, id)
    if (!elementId) return

    const element = document.getElementById(elementId)
    if (!element) return

    const scrollParent = getScrollableAncestor(element)
    if (!scrollParent) return

    const top = getOffsetTopRelativeTo(element, scrollParent)

    const offset =
      headerBottomMargin + (area === "sliceFieldSet" ? headerHeight : 0)

    const targetScrollTop = top - offset

    if (scrollParent.scrollTop === targetScrollTop) return

    scrollParent.scrollTop = targetScrollTop
  }, [area, selection])
}
